import React, { useState, useEffect } from 'react';
import { Box, Typography, Input, Button, Sheet, IconButton } from '@mui/joy';
import axios from 'axios';
import domain from "../../../util/domain";
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const formatPhoneId = (id) => {
    const numStr = id.toString();
    return numStr.startsWith('91') ? numStr.slice(2) : numStr;
};

const ReplyChat = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { originalMessage, fromId, fromName } = location.state || {};
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!originalMessage) {
            navigate('/whatsapp/chat');
            return;
        }
        fetchMessageHistory();
    }, [originalMessage]);

    const fetchMessageHistory = async () => {
        try {
            console.log(originalMessage);
            // Fetch messages filtered by contactId on the server
            const response = await axios.get(`${domain}/whatsapp/chat/messages`, {
                params: {
                    page: 1,
                    limit: 50,
                    contactId: fromId
                }
            });

            // Handle paginated response format - server already filters and validates JSON body
            const responseData = response.data.data || response.data;

            // Parse the JSON text and extract the body for display
            const processedMessages = responseData.map(message => {
                try {
                    const parsedText = JSON.parse(message.text);
                    return {
                        ...message,
                        text: parsedText.body
                    };
                } catch (e) {
                    return message;
                }
            });

            setMessages(processedMessages);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching message history:', error);
            setLoading(false);
        }
    };

    const sendReply = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;
        //check if last message received is greater than 24 hours we cant send reply.

        //get the last message received
        const lastMessagesReceived = messages.filter(message => message.from_id === fromId);
        const lastmessageReceived = lastMessagesReceived[0];

        // Check if there's a last message received before checking time difference
        if (lastmessageReceived && lastmessageReceived.createdAt) {
            console.log("Last message received:", JSON.stringify(lastmessageReceived));
            const lastMessageDate = new Date(lastmessageReceived.createdAt);
            const currentDate = new Date();
            const timeDifference = currentDate - lastMessageDate;
            const hoursDifference = timeDifference / (1000 * 60 * 60);
            if (hoursDifference > 24) {
                alert("You can't send a reply more than 24 hours after the last message was received.");
                return;
            }
        }
        try {
            await axios.post(`${domain}/whatsapp/webhook/sendcustommessage`, {
                phoneNumber: parseInt(originalMessage.from_id),
                message: newMessage,
                phone_number_id: parseInt(originalMessage.phone_number_id),
                from_id: parseInt(originalMessage.to_id),
                type: 'text'
            });


            setNewMessage('');
            fetchMessageHistory();
        } catch (error) {
            if (error.response.status === 401) {
                alert("You are not authorized to send messages.");
                navigate('/login');
            } else if (error.response.status === 500) {
                if (error.response.data.message === "Optout") {
                    alert("The user has opted out of receiving messages. We cannot send a reply to this student.");
                } else {
                    alert(error.response.data.message);
                }

            } else {
                console.error('Error sending reply:', error);
            }
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    return (
        <Sheet
            variant="outlined"
            sx={{
                maxWidth: 600,
                mx: 'auto',
                my: 4,
                py: 3,
                px: 2,
                borderRadius: 'md',
                boxShadow: 'md',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton
                    onClick={() => navigate('/whatsapp/chat')}
                    sx={{ mr: 2 }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography level="h4" component="h1">
                    Chat with {fromName}
                </Typography>
            </Box>

            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    Original Message:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Typography level="body-xs" sx={{ color: 'text.secondary', fontWeight: 'md' }}>
                        {originalMessage.from_name}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        • {formatPhoneId(parseInt(originalMessage.from_id))}
                    </Typography>
                </Box>
                <Typography level="body-md" sx={{ mt: 1 }}>
                    {originalMessage.text}
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 1 }}>
                    {new Date(originalMessage.createdAt).toLocaleString()}
                </Typography>
            </Box>

            <Box
                sx={{
                    height: 400,
                    overflowY: 'auto',
                    bgcolor: 'background.level1',
                    p: 2,
                    borderRadius: 'md',
                    mb: 2,
                    display: 'flex',
                    flexDirection: 'column-reverse'
                }}
            >
                {messages.map((message) => (
                    <Box
                        key={message.id}
                        sx={{
                            mb: 2,
                            p: 1.5,
                            bgcolor: message.from_id === fromId ? 'background.level2' : 'primary.softBg',
                            borderRadius: 'lg',
                            maxWidth: '80%',
                            ml: message.from_id === fromId ? 0 : 'auto',
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                bottom: 12,
                                [message.from_id === fromId ? 'left' : 'right']: -6,
                                width: 12,
                                height: 12,
                                bgcolor: message.from_id === fromId ? 'background.level2' : 'primary.softBg',
                                transform: 'rotate(45deg)',
                                borderRadius: '2px'
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography level="body-xs" sx={{ color: 'text.secondary', fontWeight: 'md' }}>
                                {message.from_name}
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                • {formatPhoneId(parseInt(message.from_id))}
                            </Typography>
                        </Box>
                        <Typography level="body-md" sx={{ my: 0.5 }}>
                            {message.text}
                        </Typography>
                        <Typography level="body-xs" sx={{ textAlign: 'right', color: 'text.tertiary' }}>
                            {new Date(message.createdAt).toLocaleString()}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <form onSubmit={sendReply}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Input
                        placeholder="Type your reply..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        sx={{ flex: 1 }}
                        required
                    />
                    <Button type="submit" disabled={!newMessage.trim()}>
                        Send Reply
                    </Button>
                </Box>
            </form>
        </Sheet>
    );
};

export default ReplyChat; 